import { DoubleSide, Mesh, ShaderMaterial, Vector2, Vector3, type BufferGeometry } from 'three';

/** Fixed optical panes belong to the cabin. Reflections stay at grazing angles
 * and the forward view remains clear; there is no floating glass UI layer. */
export function createWindshield(geometry: BufferGeometry) {
  const material = new ShaderMaterial({
    transparent: true, premultipliedAlpha: true, depthWrite: false,
    side: DoubleSide, forceSinglePass: true, toneMapped: false,
    uniforms: {
      uSunDirection: { value: new Vector3(-.8, .45, .7).normalize() },
      uExterior: { value: null }, uResolution: { value: new Vector2(1, 1) },
    },
    vertexShader: `varying vec2 vUv; varying vec3 vNormal; varying vec3 vPosition;
      void main() { vUv = uv; vNormal = normalize(normalMatrix * normal);
        vec4 p = modelViewMatrix * vec4(position, 1.0); vPosition = p.xyz; gl_Position = projectionMatrix * p; }`,
    fragmentShader: `
      uniform sampler2D uExterior; uniform vec2 uResolution; uniform vec3 uSunDirection;
      varying vec2 vUv; varying vec3 vNormal; varying vec3 vPosition;
      void main() {
        vec3 eye = normalize(-vPosition), normal = normalize(vNormal);
        if (dot(normal, eye) < 0.0) normal = -normal;
        float g = clamp(1.0 - dot(normal, eye), 0.0, 1.0), fresnel = g*g*g;
        float edge = 1.0 - smoothstep(0.0, 0.045, min(vUv.x, 1.0-vUv.x));
        vec3 halfVector = eye + uSunDirection; halfVector /= max(length(halfVector), 0.0001);
        float spec = clamp(dot(normal, halfVector), 0.0, 1.0);
        float s2 = spec*spec, s4 = s2*s2, s8 = s4*s4;
        vec2 uv = gl_FragCoord.xy / uResolution;
        vec3 outside = texture2D(uExterior, clamp(uv + normal.xy * edge * 0.55 / uResolution, vec2(0.0), vec2(1.0))).rgb;
        vec3 original = texture2D(uExterior, uv).rgb;
        float absorption = 0.002 + fresnel*0.012 + edge*0.006;
        vec3 reflection = vec3(0.10,0.24,0.25) * (edge*0.012 + fresnel*0.007) + vec3(0.6,0.67,0.65)*s8*s8*0.003;
        gl_FragColor = vec4((outside-original)*(1.0-absorption) + reflection, absorption);
      }`,
  });
  const mesh = new Mesh(geometry, material); mesh.name = 'survey_windshield'; mesh.userData.canopyPane = true; mesh.frustumCulled = false;
  return mesh;
}
